import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { ApiError } from '@/api/http';
import { queryKeyRoots } from '@/api/queryKeys';
import { reorderQuestionSet, type Question, type QuestionSetDetail } from '@/api/questionSets';
import { Button } from '@/components/ui/Button';
import { Table, type Column } from '@/components/ui/Table';
import { cn } from '@/lib/cn';
import { type } from '@/theme/typography';

import { QuestionMenu } from './QuestionMenu';

interface Props {
  setId: number;
  /**
   * ⚠️ **The whole composition as the server sent it, archived rows included, in `position` order** —
   * not the rows on screen. The reorder body is a permutation over all of it, so this is what a move is
   * built from, and `questionSetQuery` asks with `include_archived=true` for exactly that reason.
   */
  questions: Question[];
  /** The rows to render: the composition, minus the archived ones while *Show archived* is off. */
  visible: Question[];
  /** Whether *Show archived* is off, which is what decides whether a removal takes a row off screen. */
  hidesArchived: boolean;
  /** Whether this viewer may write the **set** — §5: the controls are **hidden**, never disabled. */
  canWrite: boolean;
  empty: ReactNode;
}

/**
 * The order §6.9 puts in a person's hands, and the order the freeze reads.
 *
 * ⚠️ **`Question.position` is what `create_interview` walks** (ADR 0004, feedback protection #1), so
 * this is the screen that decides what an interview arranged **afterwards** asks, in what order. It
 * changes **no** interview that already exists: those scores and their positions were materialised when
 * each was arranged, and the sentence saying so renders above this table (`SetScreen`).
 *
 * ⚠️ **`position` is the question's own again and an archived row keeps it** (2026-09-24, ADR 0019).
 * It lived on `QuestionSetItem` from 2026-08-11 because one question could sit in several sets at
 * different places; no two sets share a question now, so there is one place for the integer to live and
 * nothing to arbitrate. The consequence a reader can see is that *Remove* does not sink a row to the
 * end and *Restore* does not append it: a removed question is still third, and comes back third.
 *
 * ⚠️ **Move up / Move down, and there is no drag — an impossibility rather than a deferral.** TASK-076
 * recorded it when the bank had an order: a sortable row needs a ref on its `<tr>`, §4's `Table` owns
 * every `<tr>` and exposes no row ref, and changing a shared primitive for one screen's convenience is
 * a different task. ⚠️ **`@dnd-kit/core`, `/sortable` and `/utilities` are uninstalled** (2026-09-28,
 * BUG-080; this paragraph said they were still installed and that this screen did not change that) —
 * and the impossibility above is what makes that safe rather than merely tidy: the drag could not have
 * been built on them without changing `Table` first, so no screen was ever one import away from it.
 *
 * ⚠️ **The body is the full permutation of the set's question ids, archived ones included**, checked by
 * the server against the set's own composition as an **equality**. So the ids sent are never only the
 * ids on screen, and nothing partial is ever sent — a body built from a read that omitted the archived
 * rows is **missing ids and is a 422**, which is why the read is not the toggle's to narrow.
 *
 * ⚠️ **A move is relative to the rows the reader can *see*, and it is written into the *full* order.**
 * With *Show archived* off, an archived row can sit between two visible ones; *"move up"* then means
 * *"go above the row you are below on screen"*, so the moved id is spliced to the full-order index of
 * the previous **visible** row rather than one step back in the full list. Anything else would be a
 * control that sometimes appears to do nothing, which is worse than one that refuses.
 *
 * ⚠️ **Moves are serialised, and this is the client half of the race TASK-076 demonstrated with a probe
 * rather than argued.** Two nudges in quick succession would otherwise both build a permutation from
 * the list on screen, and the second would be built from an order the server had already superseded —
 * a 422 at best and a silent reshuffle at worst. Two things prevent it, and both are needed: the moves
 * run **one at a time**, chained on the previous one's promise; and each is built from the **last order
 * the server confirmed** (`confirmed`), which is the reorder's own answer rather than a cache write or
 * a refetch that may not have landed yet.
 *
 * ⚠️ **Both controls on every row are `aria-busy` while a move is in flight**, because a reflow that
 * has not happened yet is a control that looks broken; and the result is **announced**, because a
 * reflow is not feedback for a reader who cannot see it.
 */
export function SetItems({ setId, questions, visible, hidesArchived, canWrite, empty }: Props) {
  const { t } = useTranslation(['questions', 'components']);
  const queryClient = useQueryClient();

  const [announcement, setAnnouncement] = useState('');

  /** The live region, and the focus target of last resort when a row leaves the table. */
  const region = useRef<HTMLParagraphElement>(null);

  /**
   * The `⋯` of each row on screen, by question id — the control focus goes to when the row it belonged
   * to leaves. A row that unmounts stores `null` rather than dropping its entry, so nothing here can
   * hand focus to a detached node.
   */
  const menuTriggers = useRef(new Map<number, HTMLButtonElement | null>());

  /** A row on its way off screen: its id and the place it held, held until the refetch removes it. */
  const [leaving, setLeaving] = useState<{ id: number; place: number } | null>(null);

  /**
   * ⚠️ **The last order the *server* confirmed, and the reason it is a ref rather than state.** It is
   * not rendered — the rows on screen are the query's, as they must be — it exists only so the **next**
   * permutation is built from what the server holds rather than from what a refetch has managed to
   * deliver. Writing it into the cache instead would be `setQueryData` after a write, which this
   * project has refused on every screen: it would put an order on screen the reader was never told the
   * server agreed to.
   */
  const confirmed = useRef<number[] | null>(null);

  /** The promise every move waits on, which is what makes two nudges one after the other and not two. */
  const chain = useRef<Promise<unknown>>(Promise.resolve());

  const reorder = useMutation<QuestionSetDetail, ApiError, number[]>({
    mutationFn: (questionIds) => reorderQuestionSet(setId, { question_ids: questionIds }),
    onSuccess: (detail) => {
      confirmed.current = detail.questions.map((question) => question.id);
      /*
       * The set alone: an order is not a count, so no vacancy's `active_question_count` moves and
       * invalidating `roles` would be a request that can only confirm what §6.8 already holds.
       */
      void queryClient.invalidateQueries({ queryKey: queryKeyRoots.questionSets });
    },
  });

  const order = () => confirmed.current ?? questions.map((question) => question.id);

  const move = (question: Question, delta: -1 | 1) => {
    const moved = t(
      delta === -1 ? 'questions:sets.order.movedUp' : 'questions:sets.order.movedDown',
      { text: question.text },
    );

    /*
     * ⚠️ **Which rows are on screen is read now; *where* they sit is read when the move runs.** The
     * membership is a fact about the toggle and cannot change under a reorder; the **order** is the
     * server's and may already have moved on, so resolving the neighbour against the rendered list
     * would be exactly the stale-read bug `confirmed` exists to prevent, one level up.
     */
    const onScreen = new Set(visible.map((row) => row.id));

    chain.current = chain.current
      // A refused move must not stop the next one: the reader pressed a second control and is owed an
      // answer about it, and the server has already told the first one what it thought.
      .catch(() => undefined)
      .then(async () => {
        const current = order();
        // The visible rows **in the order the server last confirmed** — what *"the row above"* means.
        const seen = current.filter((id) => onScreen.has(id));
        const neighbour = seen[seen.indexOf(question.id) + delta];

        const from = current.indexOf(question.id);
        const to = neighbour === undefined ? -1 : current.indexOf(neighbour);
        if (from === -1 || to === -1) return;

        const next = [...current];
        next.splice(to, 0, ...next.splice(from, 1));

        try {
          await reorder.mutateAsync(next);
          setAnnouncement(moved);
        } catch {
          setAnnouncement(t('questions:sets.order.failed'));
        }
      });
  };

  useEffect(() => {
    if (leaving === null) return;
    // The refetch has not landed yet: the row is still there, and so is the control focus is on.
    if (visible.some((question) => question.id === leaving.id)) return;

    setLeaving(null);
    const next = visible[Math.min(leaving.place, visible.length - 1)];
    /*
     * ⚠️ **The row that takes its place, and the region when there is none.** On this screen the
     * controls are the **set's** rather than the row's — either every row carries them or none does —
     * so the *"the next row has no menu"* case §6.9 met on the library cannot arise from authorship
     * here. What can arise is the removed row being the last one, and the fallback is the same
     * deliberate place: the region that has just said what happened, immediately before the table.
     */
    const target = (next && menuTriggers.current.get(next.id)) ?? region.current;
    target?.focus();
  }, [leaving, visible]);

  const archivedInk = (question: Question) => (question.is_active ? undefined : 'text-ink-500');

  const columns: Array<Column<Question>> = [
    {
      key: 'position',
      header: t('questions:sets.items.columns.position'),
      cell: (question) => (
        <span className={cn(type.numeral, archivedInk(question))}>{question.position}</span>
      ),
    },
    {
      key: 'text',
      header: t('questions:table.columns.question'),
      primary: true,
      cell: (question) => <span className={archivedInk(question)}>{question.text}</span>,
    },
    {
      key: 'category',
      header: t('questions:table.columns.category'),
      cell: (question) => (
        <span className={archivedInk(question)}>
          {t(`components:category.${question.category}`)}
        </span>
      ),
    },
    {
      key: 'difficulty',
      header: t('questions:table.columns.difficulty'),
      cell: (question) => (
        <span className={archivedInk(question)}>
          {t(`components:difficulty.${question.difficulty}`)}
        </span>
      ),
    },
    {
      key: 'state',
      header: t('questions:table.columns.state'),
      /*
       * ⚠️ **An archived question keeps its place in its set and is not frozen into new interviews** —
       * two facts the reader has to be able to tell apart, so the state is a word rather than a
       * treatment. `whitespace-nowrap` for §6.9's measured reason: the cell's own break rule would
       * otherwise split a one-word state down to a letter's width.
       */
      cell: (question) => (
        <span className={cn(type.eyebrow, 'whitespace-nowrap')}>
          {question.is_active ? t('questions:state.active') : t('questions:state.archived')}
        </span>
      ),
    },
    {
      key: 'actions',
      header: <span className="sr-only">{t('questions:table.columns.actions')}</span>,
      align: 'right',
      cell: (question) => {
        // §5: hidden, never disabled. A reader who may not write the set sees no control at all — and
        // the gate is the **set's**, so this is all rows or none rather than a column that flickers.
        if (!canWrite) return null;

        const place = visible.findIndex((row) => row.id === question.id);

        return (
          <div className="flex items-center justify-end gap-1">
            <Button
              variant="ghost"
              size="sm"
              aria-label={t('questions:sets.order.up', { text: question.text })}
              aria-busy={reorder.isPending}
              disabled={place === 0}
              onClick={() => move(question, -1)}
            >
              <ChevronUp size={16} strokeWidth={1.5} aria-hidden />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              aria-label={t('questions:sets.order.down', { text: question.text })}
              aria-busy={reorder.isPending}
              disabled={place === visible.length - 1}
              onClick={() => move(question, 1)}
            >
              <ChevronDown size={16} strokeWidth={1.5} aria-hidden />
            </Button>
            <QuestionMenu
              setId={setId}
              question={question}
              triggerRef={(node) => {
                menuTriggers.current.set(question.id, node);
              }}
              onStateChanged={(changed, nowActive) => {
                setAnnouncement(
                  t(
                    nowActive
                      ? 'questions:restore.announced'
                      : hidesArchived
                        ? 'questions:remove.announcedHidden'
                        : 'questions:remove.announced',
                    { text: changed.text },
                  ),
                );
                /*
                 * The row is still on screen at this moment — the invalidation has only been asked for
                 * — so the focus move waits for the **rows** to change. Anything done earlier is
                 * overwritten by the unmount, which is the ordering §6.9 had to learn in a browser.
                 * A restore, and a removal with *Show archived* on, leave the row where it is: the
                 * effect above finds it still listed and does nothing, and `Dialog` hands focus back
                 * to the `⋯` it was opened from.
                 */
                const place = visible.findIndex((row) => row.id === changed.id);
                setLeaving({ id: changed.id, place: Math.max(place, 0) });
                // The order the server holds may have changed under us; the next permutation must be
                // built from the refetch rather than from a list built before the write.
                confirmed.current = null;
              }}
            />
          </div>
        );
      },
    },
  ];

  return (
    <>
      {/* Always mounted, `sr-only`, empty until something happens: a live region added to the page
          together with its first message is not reliably announced, and one that is already there is. */}
      <p ref={region} tabIndex={-1} role="status" aria-live="polite" className="sr-only">
        {announcement}
      </p>

      <Table columns={columns} rows={visible} rowKey={(question) => question.id} empty={empty} />
    </>
  );
}
